/**
 * Messages between the app's assistant page shown in an iframe and the page framing it:
 * the browser extension's side panel. The frame has no session of its own; the parent
 * hands it tokens, and keeps the refresh token to itself. It is the only page nginx
 * lets be framed.
 */
export const EMBED_ASSISTANT_PATH = '/embed/assistant';

export const EmbedMessageType = {
  /** Frame to parent: listening, send a token. */
  Ready: 'nexus-embed:ready',
  /** Frame to parent: the token is about to expire, send a fresh one. */
  TokenRequest: 'nexus-embed:token-request',
  /** Parent to frame. */
  Token: 'nexus-embed:token',
  /** Frame to parent: whether the user may use the assistant, sent when it changes. */
  Access: 'nexus-embed:access',
} as const;

export interface EmbedTokenMessage {
  type: typeof EmbedMessageType.Token;
  accessToken: string;
  idToken: string;
}

export interface EmbedAccessMessage {
  type: typeof EmbedMessageType.Access;
  allowed: boolean;
  /** Why not, e.g. USER_DISABLED; only when not allowed. */
  reason?: string;
}

export function isEmbedPage(pathname = window.location.pathname): boolean {
  return pathname.replace(/\/$/, '') === EMBED_ASSISTANT_PATH;
}

export function isEmbedTokenMessage(data: unknown): data is EmbedTokenMessage {
  const message = data as Partial<EmbedTokenMessage> | null;
  return (
    typeof message === 'object' &&
    message !== null &&
    message.type === EmbedMessageType.Token &&
    typeof message.accessToken === 'string' &&
    !!message.accessToken &&
    typeof message.idToken === 'string' &&
    !!message.idToken
  );
}

/** An exact origin, or `scheme://*` for any origin of that scheme, e.g. any extension. */
export function isAllowedParentOrigin(
  origin: string,
  allowed: readonly string[]
): boolean {
  return allowed.some((entry) =>
    entry.endsWith('://*')
      ? origin.startsWith(entry.slice(0, -1)) &&
        origin.length > entry.length - 1
      : entry === origin
  );
}

/** A JWT's claims, unverified: the API verifies the token, the frame only reads it. */
export function jwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(
        atob(payload),
        (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
      ).join('')
    );
    const claims: unknown = JSON.parse(json);
    return typeof claims === 'object' && claims !== null
      ? (claims as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
